import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { HardDrive, Paperclip, X } from "lucide-react";

interface AttachmentsGroupProps {
  disabled: boolean;
  isUploading: boolean;
  attachedFilesCount: number;
  attachedFiles: Map<string, any>;
  onFileAttachment: () => void;
  onAttachmentRemove: (id: string) => void;
  onGoogleDriveClick: () => void;
  showGoogleDrive: boolean;
}

export function AttachmentsGroup({
  disabled,
  isUploading,
  attachedFilesCount,
  attachedFiles,
  onFileAttachment,
  onAttachmentRemove,
  onGoogleDriveClick,
  showGoogleDrive,
}: AttachmentsGroupProps) {
  const [showAttachmentsTooltip, setShowAttachmentsTooltip] = useState(false);

  return (
    <>
      {/* File Attachments */}
      <div className="relative">
        <ResponsivePopover
          open={showAttachmentsTooltip}
          onOpenChange={setShowAttachmentsTooltip}
          content={
            attachedFilesCount > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Attached Files</h4>
                  <Badge variant="secondary" className="text-xs">
                    {attachedFilesCount} file{attachedFilesCount !== 1 ? "s" : ""}
                  </Badge>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {Array.from(attachedFiles.entries()).map(([id, data]) => (
                    <div
                      key={id}
                      className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{data.file.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {(data.blob.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onAttachmentRemove(id)}
                        className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground" title="Close"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null
          }
          title="Attached Files"
          className="w-80 p-3"
          popoverSide="bottom"
          align="start"
          asChild
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onFileAttachment}
            disabled={disabled || isUploading}
            className="h-8 w-8 p-0 flex-shrink-0"
            title={isUploading ? "Processing files..." : "Attach Files"}
          >
            {isUploading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Paperclip className="h-4 w-4" />
            )}
          </Button>
        </ResponsivePopover>
        {attachedFilesCount > 0 && (
          <div className="absolute -top-1 -right-1 h-4 w-4 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {attachedFilesCount}
          </div>
        )}
      </div>

      {/* Google Drive */}
      {showGoogleDrive && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onGoogleDriveClick}
          disabled={disabled || isUploading}
          className="h-8 w-8 p-0 flex-shrink-0"
          title={isUploading ? "Processing file..." : "Attach from Google Drive"}
        >
          {isUploading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <HardDrive className="h-4 w-4" />
          )}
        </Button>
      )}
    </>
  );
}
